import React, { useState, useEffect } from 'react';
import { Button } from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';
import notify from 'devextreme/ui/notify';
import { Capacitor } from '@capacitor/core';
import useAppUpdate from '../../hooks/useAppUpdate';
import './app-settings.scss';

const AppSettings = () => {
    const { checkForUpdate, getAppInfo, openAppStore, resetUpdateSettings, getUpdateStats } = useAppUpdate();
    const [isLoading, setIsLoading] = useState(false);
    const [appInfo, setAppInfo] = useState(null);
    const [updateStats, setUpdateStats] = useState(null);

    useEffect(() => {
        loadAppInfo();
        loadUpdateStats();
    }, []);

    const loadAppInfo = async () => {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        try {
            const info = await getAppInfo();
            setAppInfo(info);
        } catch (error) {
            console.error('Failed to load app info:', error);
        }
    };

    const loadUpdateStats = async () => {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        try {
            const stats = getUpdateStats();
            setUpdateStats(stats);
        } catch (error) {
            console.error('Failed to load update stats:', error);
        }
    };

    const handleCheckForUpdates = async () => {
        if (!Capacitor.isNativePlatform()) {
            notify('Update-Prüfung ist nur in der mobilen App verfügbar.', 'info', 3000);
            return;
        }

        setIsLoading(true);
        try {
            const updateFound = await checkForUpdate();
            if (updateFound) {
                notify('Update gefunden! Der Update-Dialog wird angezeigt.', 'success', 3000);
            } else {
                notify('Ihre App ist auf dem neuesten Stand.', 'success', 3000);
            }
            // Stats nach Prüfung aktualisieren
            loadUpdateStats();
        } catch (error) {
            console.error('Error checking for updates:', error);
            notify('Fehler bei der Update-Prüfung.', 'error', 3000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenStore = async () => {
        if (!Capacitor.isNativePlatform()) {
            notify('Store-Link ist nur in der mobilen App verfügbar.', 'info', 3000);
            return;
        }

        try {
            await openAppStore();
        } catch (error) {
            console.error('Error opening store:', error);
            notify('Fehler beim Öffnen des App Stores.', 'error', 3000);
        }
    };

    const handleResetSettings = () => {
        if (!Capacitor.isNativePlatform()) {
            notify('Einstellungen zurücksetzen ist nur in der mobilen App verfügbar.', 'info', 3000);
            return;
        }

        try {
            resetUpdateSettings();
            loadUpdateStats();
            notify('Update-Einstellungen wurden zurückgesetzt.', 'success', 3000);
        } catch (error) {
            console.error('Error resetting settings:', error);
            notify('Fehler beim Zurücksetzen der Einstellungen.', 'error', 3000);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Nie';
        return date.toLocaleString('de-DE');
    };

    return (
        <div className="app-settings-container">
            <div className="settings-card">
                <h2>App-Einstellungen</h2>
                
                {Capacitor.isNativePlatform() ? (
                    <>
                        {appInfo && (
                            <div className="app-info-section">
                                <h3>App-Informationen</h3>
                                <div className="info-row">
                                    <span className="label">Name:</span>
                                    <span className="value">{appInfo.name}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Version:</span>
                                    <span className="value">{appInfo.version}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Build:</span>
                                    <span className="value">{appInfo.build}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Bundle ID:</span>
                                    <span className="value">{appInfo.id}</span>
                                </div>
                            </div>
                        )}

                        <div className="update-section">
                            <h3>App-Updates</h3>
                            <p className="update-description">
                                Überprüfen Sie, ob eine neue Version der App verfügbar ist.
                                Updates werden automatisch beim App-Start geprüft, Sie können aber auch manuell nach Updates suchen.
                            </p>
                            
                            <div className="button-group">
                                <Button
                                    text="Nach Updates suchen"
                                    type="default"
                                    stylingMode="contained"
                                    onClick={handleCheckForUpdates}
                                    disabled={isLoading}
                                    icon="refresh"
                                    className="update-button"
                                />
                                
                                <Button
                                    text="App Store öffnen"
                                    type="normal"
                                    stylingMode="outlined"
                                    onClick={handleOpenStore}
                                    disabled={isLoading}
                                    icon="cart"
                                    className="store-button"
                                />
                            </div>

                            {updateStats && (
                                <div className="update-stats">
                                    <h4>Update-Statistiken</h4>
                                    <div className="stats-row">
                                        <span className="stats-label">Letzte Prüfung:</span>
                                        <span className="stats-value">{formatDate(updateStats.lastCheck)}</span>
                                    </div>
                                    <div className="stats-row">
                                        <span className="stats-label">Letzter Dialog:</span>
                                        <span className="stats-value">{formatDate(updateStats.lastDialogShown)}</span>
                                    </div>
                                    {updateStats.dismissedVersion && (
                                        <div className="stats-row">
                                            <span className="stats-label">Ignorierte Version:</span>
                                            <span className="stats-value">{updateStats.dismissedVersion}</span>
                                        </div>
                                    )}
                                    <div className="stats-row">
                                        <span className="stats-label">Auto-Prüfung:</span>
                                        <span className={`stats-value ${updateStats.autoCheckEnabled ? 'enabled' : 'disabled'}`}>
                                            {updateStats.autoCheckEnabled ? 'Aktiviert' : 'Deaktiviert'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="advanced-section">
                                <Button
                                    text="Update-Einstellungen zurücksetzen"
                                    type="normal"
                                    stylingMode="text"
                                    onClick={handleResetSettings}
                                    disabled={isLoading}
                                    icon="clearformat"
                                    className="reset-button"
                                />
                                <p className="reset-description">
                                    Setzt alle gespeicherten Update-Einstellungen zurück. 
                                    Ignorierte Versionen werden wieder angezeigt.
                                </p>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="web-info">
                        <h3>Web-Version</h3>
                        <p>
                            Sie verwenden die Web-Version der App. 
                            Update-Funktionen sind nur in der mobilen App verfügbar.
                        </p>
                        <p>
                            Die Web-Version wird automatisch aktualisiert, 
                            wenn Sie die Seite neu laden.
                        </p>
                        <Button
                            text="Seite neu laden"
                            type="default"
                            stylingMode="contained"
                            onClick={() => window.location.reload()}
                            icon="refresh"
                            className="refresh-button"
                        />
                    </div>
                )}
            </div>

            <LoadPanel
                visible={isLoading}
                showIndicator={true}
                shading={true}
                message="Suche nach Updates..."
            />
        </div>
    );
};

export default AppSettings;
